import React, { useEffect, useState } from "react";
import { Plus, Trash2, Link2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

/**
 * URL + Remarks links dialog for a Content row.
 * `item.references` is a list of { id, url, description(=remark) }.
 */
export default function ContentLinksDialog({ open, onOpenChange, item, onSave }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open || !item) return;
    setItems(Array.isArray(item.references) ? item.references.map((r) => ({ ...r })) : []);
  }, [open, item]);

  const addRow = () =>
    setItems((p) => [
      ...p,
      { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-4)}`, url: "", description: "" },
    ]);
  const updateRow = (id, k, v) => setItems((p) => p.map((it) => (it.id === id ? { ...it, [k]: v } : it)));
  const removeRow = (id) => setItems((p) => p.filter((it) => it.id !== id));

  const submit = async () => {
    try {
      const cleaned = items
        .map((r) => ({ id: r.id, url: (r.url || "").trim(), description: (r.description || "").trim(), status: r.status || "" }))
        .filter((r) => r.url || r.description);
      await onSave({ references: cleaned });
      onOpenChange(false);
      toast.success("Links saved");
    } catch {
      toast.error("Failed to save");
    }
  };

  const openUrl = (url) => {
    let u = (url || "").trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
    window.open(u, "_blank", "noopener,noreferrer");
  };

  if (!item) return null;
  const COLS = "1fr 1fr 34px 34px";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="content-links-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Link2 className="h-5 w-5 text-blue-600" />
            Links for {item.task || "this row"}
          </DialogTitle>
          <DialogDescription>Add URLs with a remark for each.</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div
            className="grid gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-3 h-10 items-center bg-slate-100/70 border-b border-slate-200"
            style={{ gridTemplateColumns: COLS }}
          >
            <div>URL</div>
            <div>Remarks</div>
            <div></div>
            <div></div>
          </div>
          {items.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-slate-400" data-testid="content-links-empty">
              No links yet — click "Add link".
            </div>
          ) : (
            items.map((it) => (
              <div
                key={it.id}
                className="grid items-center gap-2 px-3 h-12 border-b border-slate-100 group"
                style={{ gridTemplateColumns: COLS }}
              >
                <Input
                  value={it.url}
                  onChange={(e) => updateRow(it.id, "url", e.target.value)}
                  placeholder="https://…"
                  className="h-8 text-sm"
                  data-testid={`content-link-url-${it.id}`}
                />
                <Input
                  value={it.description}
                  onChange={(e) => updateRow(it.id, "description", e.target.value)}
                  placeholder="Remark…"
                  className="h-8 text-sm"
                  data-testid={`content-link-remark-${it.id}`}
                />
                <button
                  onClick={() => openUrl(it.url)}
                  disabled={!(it.url || "").trim()}
                  className="h-8 w-8 rounded-md hover:bg-sky-50 text-slate-400 hover:text-blue-600 flex items-center justify-center disabled:opacity-30"
                  title="Open link"
                  data-testid={`content-link-open-${it.id}`}
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
                <button
                  onClick={() => removeRow(it.id)}
                  className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all"
                  data-testid={`content-link-remove-${it.id}`}
                  title="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>

        <div>
          <Button variant="outline" size="sm" onClick={addRow} data-testid="content-link-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add link
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="content-links-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
